import axios, { AxiosResponse } from "axios";
import { WebAPI, IFailCallbackParam } from "../constants";
import { getErrorStatus } from "./getErrorStatus";
import AuthService from "../../common/AuthService";
import { ProjaectMemo, ProjaectName } from "./common";

interface ICallbackParams extends IFailCallbackParam {
  /**
   * WebAPIでプロジェクト作成が成功した場合に呼び出されます
   * @callback success
   * @param {number} status - httpステータス
   */
  success: (status: number) => void;
}

export interface ICreateProjectRequesuModel {
  /**
   * プロジェクト名
   */
  name: string;
  /**
   * メモ
   */
  memo: string;
}

function AxiosPost(
  url: string,
  params: ICreateProjectRequesuModel,
  token?: string
) {
  if (token) {
    return axios.post(url, JSON.stringify(params), {
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
      },
    });
  } else {
    return axios.post(url, JSON.stringify(params), {
      headers: { "Content-Type": "application/json" },
    });
  }
}

interface IAxiosInternalParams extends ICallbackParams {
  promise: Promise<AxiosResponse>;
}

function AxiosInternal(params: IAxiosInternalParams) {
  params.promise
    .then((res) => {
      params.success(res.status);
    })
    .catch((err) => {
      const { id, status } = getErrorStatus(err);
      params.fail(id, status);
    });
}

export interface ICreateProjectParams
  extends ICreateProjectRequesuModel,
    ICallbackParams {
  /**
   * 認証オブジェクト
   */
  authService?: AuthService;
}

/**
 * プロジェクトを作成します
 * @function createProject
 * @param {ICreateProjectParams} params - プロジェクト作成パラメータ
 */
export function createProject(params: ICreateProjectParams): void {
  const { authService, name, memo } = params;
  const req: ICreateProjectRequesuModel = {
    name: ProjaectName(name),
    memo: ProjaectMemo(memo),
  };
  const url = WebAPI.CREATE_PROJECT;
  if (authService) {
    authService.getToken({
      success: (token) => {
        AxiosInternal({
          promise: AxiosPost(url, req, token),
          success: params.success,
          fail: (id, status) => {
            if (status === 401) {
              createProjectRetry401(authService, url, req, params);
            } else {
              params.fail(id, status);
            }
          },
        });
      },
      fail: (err) => {
        const { id, status } = getErrorStatus(err);
        params.fail(id, status);
      },
    });
  } else {
    AxiosInternal({
      promise: AxiosPost(url, req),
      success: params.success,
      fail: params.fail,
    });
  }
}

/**
 * プロジェクトを作成します
 * @function createProjectRetry401
 * @param {AuthService} authService - 認証オブジェクト
 * @param {string} url - リクエストURL
 * @param {ICreateProjectRequesuModel} req - リクエストパラメータ
 * @param {ICallbackParams} params - コールバックパラメータ
 */
function createProjectRetry401(
  authService: AuthService,
  url: string,
  req: ICreateProjectRequesuModel,
  params: ICallbackParams
): void {
  authService.updateToken({
    success: (token) => {
      AxiosInternal({
        promise: AxiosPost(url, req, token),
        success: params.success,
        fail: params.fail,
      });
    },
    fail: (err) => {
      const { id, status } = getErrorStatus(err);
      params.fail(id, status);
    },
  });
}
